import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ChevronsUpDown, Copy, ExternalLink, Info, LogOut, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { useTheme, THEMES, type Theme } from "../../lib/theme";
import { WEB_BUILD } from "../../lib/build-info";
import { copyText } from "../../lib/clipboard";
import { InitialsAvatar } from "../../components/initials-avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { roleLabel } from "../admin-users/roles";
import { useApiVersion } from "./api";
import { Button } from "../../components/ui/button";
import { buildRows, buildText, type BuildRow } from "./build-info";

/** Where the menu renders; the sidebar stays in the DOM on a phone. */
type Placement = "sidebar" | "topbar";

const THEME_LABEL_KEY: Record<Theme, string> = {
  system: "account.themeSystem",
  light: "account.themeLight",
  dark: "account.themeDark",
  "high-contrast": "account.themeHighContrast",
};

const BUILD_LABEL_KEY: Record<"version" | "build" | "builtAt" | "source", string> = {
  version: "account.version",
  build: "account.build",
  builtAt: "account.builtAt",
  source: "account.source",
};

/** F-036 (SRS V0.9.1): Ryddion's Impressum, linked - never copied - from every view. */
const IMPRINT_URL = "https://ryddion.com/impressum/";

/**
 * The user menu (CD-009 / LP-031): the way to the own account ([F-029] -
 * display name, password, mail address, sessions), the colour mode (CD-002),
 * the build information (CD-010), the Impressum (F-036) and Abmelden. The
 * menu sits in the sidebar and the top bar, so the Impressum is one click
 * away from every view. The system account has no account page of its own
 * ([F-031]: its credentials live in the deployment).
 */
export function UserMenu({ placement }: { placement: Placement }): JSX.Element {
  const { t } = useTranslation();
  const { user, logout, isSystem } = useAuth();
  const { theme, setTheme } = useTheme();
  const [aboutOpen, setAboutOpen] = useState(false);
  const inSidebar = placement === "sidebar";

  const name = isSystem ? t("system.name") : (user?.displayName ?? "");
  const roles = isSystem
    ? t("system.role")
    : user && user.roles.length > 0
      ? user.roles.map((role) => roleLabel(t, role)).join(", ")
      : t("auth.noRoles");

  return (
    <div className="border-t p-3">
      {/* modal={false}: the menu opens a dialog, and a modal menu would leave
          the page inert behind it (Radix pointer-events lock). */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={inSidebar ? "sidebar_user-menu" : "topbar_user-menu"}
            aria-label={t("account.menu")}
            className="flex w-full items-center gap-3 rounded-md p-1.5 text-left hover:bg-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <InitialsAvatar name={name} />
            <div className="min-w-0 flex-1">
              <div
                data-testid={inSidebar ? "sidebar_user-name" : "topbar_user-name"}
                className="truncate text-sm font-medium"
              >
                {name}
              </div>
              <div
                data-testid={inSidebar ? "sidebar_user-roles" : "topbar_user-roles"}
                className="truncate text-xs text-muted-foreground"
              >
                {roles}
              </div>
            </div>
            <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={inSidebar ? "start" : "end"}
          side={inSidebar ? "top" : "bottom"}
          className="w-64"
          // Two items open a dialog. Returning focus to the trigger on close
          // would pull it out of that dialog - and Escape then no longer
          // reaches it.
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuLabel className="font-normal">
            <div className="truncate text-sm font-medium">{name}</div>
            {user?.email ? (
              <div className="truncate text-xs text-muted-foreground">{user.email}</div>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {!isSystem ? (
            <>
              <DropdownMenuItem asChild data-testid="user-menu_account">
                <Link to="/konto">
                  <UserCog aria-hidden="true" />
                  {t("account.ownAccount")}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {t("account.theme")}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={theme} onValueChange={(next) => setTheme(next as Theme)}>
            {THEMES.map((option) => (
              <DropdownMenuRadioItem
                key={option}
                value={option}
                data-testid={`user-menu_theme_${option}`}
              >
                {t(THEME_LABEL_KEY[option])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="user-menu_about" onSelect={() => setAboutOpen(true)}>
            <Info aria-hidden="true" />
            {t("account.about")}
          </DropdownMenuItem>
          <DropdownMenuItem asChild data-testid="user-menu_imprint">
            <a href={IMPRINT_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink aria-hidden="true" />
              {t("account.imprint")}
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="user-menu_sign-out" onSelect={logout}>
            <LogOut aria-hidden="true" />
            {t("auth.signOut")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}

/**
 * CD-010 / LP-032: version, build, date and source state of web and API. The
 * copy button (CD-017) puts both blocks on the clipboard as they are shown.
 */
function AboutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const api = useApiVersion(open);
  const local = t("account.local");
  const webRows = buildRows(WEB_BUILD, formatDateTime, local);
  const apiRows = api.data
    ? buildRows(
        {
          version: api.data.version,
          // `?? null`: a server from before CD-018 does not report it.
          build: api.data.build ?? null,
          commit: api.data.commit,
          tag: api.data.tag,
          builtAt: api.data.builtAt,
        },
        formatDateTime,
        local,
      )
    : [];

  const labelled = (rows: BuildRow[]) => rows.map((row) => ({ label: t(BUILD_LABEL_KEY[row.key]), value: row.value }));
  const copy = async (host: HTMLElement): Promise<void> => {
    const text = buildText(t("account.aboutTitle"), [
      { title: t("account.web"), rows: labelled(webRows) },
      { title: t("account.api"), rows: labelled(apiRows) },
    ]);
    if (await copyText(text, host)) toast.success(t("account.copied"));
    else toast.error(t("account.copyFailed"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-menu_about-dialog">
        <DialogHeader>
          <DialogTitle>{t("account.aboutTitle")}</DialogTitle>
          <DialogDescription>{t("account.aboutHint")}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
          <dt className="col-span-2 pt-1 text-xs font-medium uppercase text-muted-foreground">
            {t("account.web")}
          </dt>
          {webRows.map((row) => (
            <BuildRowView key={row.key} labelKey={BUILD_LABEL_KEY[row.key]} value={row.value} testid={`user-menu_about-web_${row.key}`} />
          ))}
          <dt className="col-span-2 pt-3 text-xs font-medium uppercase text-muted-foreground">
            {t("account.api")}
          </dt>
          {apiRows.map((row) => (
            <BuildRowView key={row.key} labelKey={BUILD_LABEL_KEY[row.key]} value={row.value} testid={`user-menu_about-api_${row.key}`} />
          ))}
        </dl>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="user-menu_about-copy"
            // Until the server answers, a copy would leave its block empty.
            disabled={api.isPending}
            onClick={(event) => void copy(event.currentTarget.parentElement ?? event.currentTarget)}
          >
            <Copy aria-hidden="true" />
            {t("account.copy")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BuildRowView({
  labelKey,
  value,
  testid,
}: {
  labelKey: string;
  value: string;
  testid: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <dt className="text-muted-foreground">{t(labelKey)}</dt>
      <dd data-testid={testid} className="font-mono text-xs leading-5">
        {value}
      </dd>
    </>
  );
}
